import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import "./Dashboard.css";

function ComprehensiveDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stdSummary, setStdSummary] = useState(null);
  const [stdError, setStdError] = useState("");

  useEffect(() => {
    // Fetch standardized summary and dashboard charts in parallel
    fetchStandardSummary();
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://restaurant-analytics-backend.onrender.com/visualize/dashboard",
        {
          method: "POST",
        }
      );
      const data = await response.json();

      if (response.ok) {
        console.log("Dashboard data received:", data);
        setDashboardData(data);
      } else {
        throw new Error(data.detail || "Failed to fetch dashboard data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStandardSummary = async () => {
    setStdError("");
    try {
      const res = await fetch("https://restaurant-analytics-backend.onrender.com/report/summary", {
        method: "GET",
      });
      if (res.status === 404) {
        // No summary yet (analysis not run this session)
        return;
      }
      const data = await res.json();
      if (res.ok) {
        setStdSummary(data);
      } else {
        throw new Error(data.detail || "Failed to fetch standardized summary");
      }
    } catch (err) {
      setStdError(err.message || String(err));
    }
  };

  const renderChart = (chartData, title) => {
    if (!chartData) return null;

    if (chartData.error) {
      return (
        <div className="chart-error">
          <h3>{title}</h3>
          <p>❌ {chartData.error}</p>
        </div>
      );
    }

    // Helper to build clean Plotly layout
    const buildLayout = (xTitle = "", yTitle = "") => ({
      title: title,
      template: "plotly_white",
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      font: {
        family: "Inter, Segoe UI, Roboto, sans-serif",
        size: 12,
        color: "#111827",
      },
      autosize: true,
      height: 520,
      margin: { t: 90, r: 80, b: 140, l: 120 },
      hovermode: "x unified",
      hoverlabel: { bgcolor: "#ffffff", font: { size: 12 } },
      legend: { orientation: "h", y: -0.35 },
      xaxis: {
        title: { text: xTitle },
        showgrid: true,
        gridcolor: "#eef2ff",
        tickangle: -30,
        automargin: true,
        ticklabeloverflow: "allow",
      },
      yaxis: {
        title: { text: yTitle },
        showgrid: true,
        gridcolor: "#f3f4f6",
        automargin: true,
      },
    });

    // Handle Plotly format (chart property with JSON string)
    if (chartData.chart) {
      try {
        const chart = JSON.parse(chartData.chart);
        const desiredHeight = Math.max(
          520,
          chart.layout?.height || (title === "Executive Summary" ? 900 : 520)
        );
        return (
          <div
            className="chart-container"
            style={{ minHeight: desiredHeight + 40 }}
          >
            <h3>{title}</h3>
            <div
              className="plot-holder"
              style={{ zIndex: 1, height: desiredHeight }}
            >
              <Plot
                data={chart.data}
                layout={{
                  ...buildLayout(
                    chart.layout?.xaxis?.title?.text || "",
                    chart.layout?.yaxis?.title?.text || ""
                  ),
                  ...chart.layout,
                  height: desiredHeight,
                }}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler={true}
                config={{ responsive: true }}
              />
            </div>
          </div>
        );
      } catch (error) {
        return (
          <div className="chart-error">
            <h3>{title}</h3>
            <p>❌ Error parsing chart data: {error.message}</p>
          </div>
        );
      }
    }

    return (
      <div className="chart-error">
        <h3>{title}</h3>
        <p>❌ Unsupported chart format</p>
      </div>
    );
  };

  // Remove duplicate KPI section per user request
  const renderKPICards = () => null;

  const renderStandardizedSummary = () => {
    if (!stdSummary) return null;
    const summaries = stdSummary.summaries || {};
    const metrics = stdSummary.metrics || {};
    const profit = summaries.profit_analysis || {};
    const profitMetrics = metrics.profit_analysis || {};

    const totalRevenue = profit.total_revenue ?? null;
    const totalProfit = profit.total_profit ?? null;
    const profitMargin = profit.profit_margin ?? null;
    const negCount = profitMetrics.items_with_negative_margin ?? null;

    return (
      <div className="summary-cards" style={{ marginTop: "1rem" }}>
        <div className="summary-card">
          <span className="summary-icon">🕒</span>
          <div className="summary-content">
            <h3>{new Date(stdSummary.generated_at).toLocaleString()}</h3>
            <p>Last Analysis</p>
          </div>
        </div>

        <div className="summary-card">
          <span className="summary-icon">📋</span>
          <div className="summary-content">
            <h3>{(stdSummary.records_analyzed ?? 0).toLocaleString()}</h3>
            <p>Records Analyzed</p>
          </div>
        </div>

        {totalRevenue != null && (
          <div className="summary-card">
            <span className="summary-icon">💰</span>
            <div className="summary-content">
              <h3>₹{Number(totalRevenue).toLocaleString()}</h3>
              <p>Total Revenue</p>
            </div>
          </div>
        )}

        {totalProfit != null && (
          <div className="summary-card">
            <span className="summary-icon">📈</span>
            <div className="summary-content">
              <h3>₹{Number(totalProfit).toLocaleString()}</h3>
              <p>Total Profit</p>
            </div>
          </div>
        )}

        {profitMargin != null && (
          <div className="summary-card">
            <span className="summary-icon">📊</span>
            <div className="summary-content">
              <h3>{Number(profitMargin).toFixed(1)}%</h3>
              <p>Profit Margin</p>
            </div>
          </div>
        )}

        {negCount != null && (
          <div className="summary-card">
            <span className="summary-icon">⚠️</span>
            <div className="summary-content">
              <h3>{Number(negCount).toLocaleString()}</h3>
              <p>Negative-Margin Items</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Comprehensive Dashboard...</h2>
        <p>Aggregating all your restaurant analytics and insights</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>❌ Error Loading Dashboard</h2>
        <p>{error}</p>
        <p>
          Make sure you have uploaded both sales data and product costs for
          comprehensive analytics.
        </p>
        <button onClick={fetchDashboardData} className="retry-btn">
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.dashboard) {
    return (
      <div className="dashboard-error">
        <h2>📈 No Dashboard Data Available</h2>
        <p>
          Please upload your sales data and product costs first to view the
          comprehensive dashboard
        </p>
      </div>
    );
  }

  const { dashboard, data_summary } = dashboardData;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>📈 Comprehensive Analytics Dashboard</h1>
        <p>
          Complete overview of your restaurant's performance, trends, and key
          insights
        </p>

        {/* Standardized summary from /report/summary (fast, consistent) */}
        {stdError ? (
          <div className="dashboard-error" style={{ padding: "0.5rem 1rem" }}>
            <p>⚠️ Summary unavailable: {stdError}</p>
          </div>
        ) : (
          renderStandardizedSummary()
        )}

        {data_summary && (
          <div className="summary-cards">
            <div className="summary-card">
              <span className="summary-icon">📋</span>
              <div className="summary-content">
                <h3>{data_summary.total_records?.toLocaleString() || "N/A"}</h3>
                <p>Total Records</p>
              </div>
            </div>

            <div className="summary-card">
              <span className="summary-icon">🏷️</span>
              <div className="summary-content">
                <h3>
                  {data_summary.unique_products?.toLocaleString() || "N/A"}
                </h3>
                <p>Unique Products</p>
              </div>
            </div>

            {data_summary.date_range && (
              <div className="summary-card">
                <span className="summary-icon">📅</span>
                <div className="summary-content">
                  <h3>
                    {new Date(
                      data_summary.date_range.start
                    ).toLocaleDateString()}{" "}
                    -{" "}
                    {new Date(data_summary.date_range.end).toLocaleDateString()}
                  </h3>
                  <p>Analysis Period</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      {renderKPICards()}

      {/* Main Charts */}
      <div className="charts-grid">
        {dashboard.main_charts?.main_dashboard &&
          renderChart(
            dashboard.main_charts.main_dashboard,
            "Executive Summary"
          )}

        {/* KPI section removed */}
      </div>

      {/* Summary Tables */}
      {dashboard.summary_tables && dashboard.summary_tables.length > 0 && (
        <div className="summary-tables">
          <h2>📋 Performance Summary</h2>
          {dashboard.summary_tables.map((table, index) => (
            <div key={index} className="table-container">
              <h3>{table.title || `Summary Table ${index + 1}`}</h3>
              {table.error ? (
                <p>❌ {table.error}</p>
              ) : (
                <div className="table-scroll">
                  {/* Table rendering would go here if provided by backend */}
                  <p>📊 Detailed performance metrics available</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="dashboard-actions">
        <button onClick={fetchDashboardData} className="refresh-btn">
          🔄 Refresh Dashboard
        </button>
      </div>
    </div>
  );
}

export default ComprehensiveDashboard;
